import type { CSSProperties, ReactNode } from 'react'

import EmptyState from '@/components/ui/EmptyState'
import { cn } from '@/lib/utils'

export type ChartStateValue = 'loaded' | 'loading' | 'failed'

interface ChartStateProps {
  state: ChartStateValue
  /** Renders "Try again" on `failed`. Omit it and the message still shows, with no
   *  button rather than a dead one — the same contract as RefreshBar.
   *
   *  Every optional below is written `| undefined` rather than plain `?`:
   *  under exactOptionalPropertyTypes a chart forwarding its own optional
   *  prop straight through — which is all any caller here does — cannot
   *  otherwise pass it. MenuItem already spells its props this way. */
  onRetry?: (() => void) | undefined
  failedMessage?: string | undefined
  /** The loading placeholder. Size it to the chart it stands in for. */
  skeleton?: ReactNode | undefined
  /**
   * The box the loaded chart fills. Both placeholder states are laid into it,
   * which is what stops the page moving when the data lands: a chart that
   * reserves nothing while loading pulls everything below it up, then shoves
   * it back down a moment later.
   */
  style?: CSSProperties | undefined
  className?: string | undefined
  children?: ReactNode | undefined
}

/**
 * Loading and Failed for a chart, wrapped around the chart the caller drew.
 * Figma ships both states on every chart component; nothing in this folder
 * had them, so every chart painted an empty box while its query was in
 * flight.
 *
 * Not CardState: that one is a card body, with a padded button, some 160px
 * before any content. A chart is 54px tall in the 52-week table and 32px in a
 * watchlist row. So Failed is EmptyState at `compact` — dot, sentence, a text
 * "Try again" — centred in the box the chart itself occupies. The same parts
 * as every other failure in the app, at the smallest scale.
 *
 * `loaded` returns the children with nothing around them. A chart is a layout
 * participant — a table cell, a flex child, an SVG sized to a measured box —
 * and a wrapper here would change the geometry it was drawn for.
 */
export default function ChartState({
  state,
  onRetry,
  failedMessage = "Couldn't load the chart",
  skeleton,
  style,
  className,
  children,
}: ChartStateProps) {
  if (state === 'loading') {
    return (
      <div aria-busy="true" style={style} className={cn('relative', className)}>
        {/* Every Skeleton is aria-hidden, so without this the chart is silent
            to a screen reader for as long as the request takes. */}
        <span role="status" className="sr-only">
          Loading…
        </span>
        {skeleton}
      </div>
    )
  }

  if (state === 'failed') {
    // Polite, not an alert: a table of failed charts would otherwise interrupt
    // the reader once per row.
    return (
      <div style={style} className={className}>
        <EmptyState kind="failed" size="compact" title={failedMessage} onRetry={onRetry} />
      </div>
    )
  }

  return <>{children}</>
}
